import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { DispatchViewModel, DispatchStatus, UpcomingDispatchesQueryResponse, DispatchesQueryResponse } from '../models/dispatch.model';
import { MOCK_UPCOMING_DISPATCHES_RESPONSE } from '../shared/mocks/upcoming-dispatches.mock';
import { UPCOMING_DISPATCHES_QUERY } from '../shared/graphql/upcoming-dispatches.query';
import moment from 'moment';
import { ApiEndpointsService } from './api-endpoints.service';
import { DISPATCHES_QUERY } from '../shared/graphql/dispatches.query';

@Injectable({ providedIn: 'root' })
export class DispatchService {
	private readonly http = inject(HttpClient);
	private readonly apiService = inject(ApiEndpointsService);

	getUpcomingDispatchesRows(args?:{limit:number}): Observable<DispatchViewModel[]> {
		// Uncomment for mock data
		//return of(this.toDispatchRows(MOCK_UPCOMING_DISPATCHES_RESPONSE).slice(0, 4));
		return this.getUpcomingDispatches().pipe(map(
			res=>{
				const rows = this.toDispatchRows(res);
				if (args?.limit) {
					return rows.slice(0, args.limit);
				}
				return rows;
			}
			), catchError((err) => {
			console.error('Failed to load widgets', err); return of<DispatchViewModel[]>([]);
		}));
	}

	private getUpcomingDispatches(): Observable<UpcomingDispatchesQueryResponse> {
		const endpoint = this.apiService.baseUrl;
		return this.http.post<UpcomingDispatchesQueryResponse>(endpoint, { query: UPCOMING_DISPATCHES_QUERY }, { withCredentials: true });
	}

	private toDispatchRows(response: UpcomingDispatchesQueryResponse): DispatchViewModel[] {
		const now = moment.utc();

		return response.data.upcomingDispatches
			.flatMap((dispatch) =>
				dispatch.event_nodes.map((node) => {
					const start = moment.utc(node.event_node_start_dttm_utc);
					const diffMinutes = start.diff(now, 'minutes');

					let status: DispatchStatus = DispatchStatus.None;
					if (diffMinutes > 30) {
						status = DispatchStatus.Upcoming;
					}

					return {
						id: node.event_node_id,
						org: node.organization_name?.[0]?.label ?? '',
						site: node.site_display_label?.[0]?.label ?? '',
						program: dispatch.program_name?.[0]?.label ?? '',
						portfolio: dispatch.portfolio_name?.[0]?.label ?? '',
						startUtc: node.event_node_start_dttm_utc,
						endUtc: node.event_node_end_dttm_utc,
						status,
						timeZone: dispatch.full_time_zone ?? '',
					};
				})
			).sort((a, b) => moment.utc(a.startUtc).diff(moment.utc(b.startUtc)));
	}
	
//NEW METHODS

private getDispatches(args?: { dateSince?: string; limit?: number }): Observable<DispatchesQueryResponse> {
  const endpoint = this.apiService.baseUrl;
  const defaultDateSince = moment.utc().subtract(30, 'minutes').toISOString();

  const body = {
    query: DISPATCHES_QUERY,
    variables: {
      dateSince: args?.dateSince ?? defaultDateSince,
      limit: args?.limit ?? 5,
    },
  };

  return this.http.post<DispatchesQueryResponse>(endpoint, body, {
    withCredentials: true,
  });
}

private toDispatchRowsFromDispatches(response: DispatchesQueryResponse ): DispatchViewModel[] {
  const now = moment();

  return response.data.dispatches.flatMap(dispatch =>
    dispatch.event_nodes.map(node => {
      const start = moment(node.event_node_start_dttm_utc);
			const end =  node.event_node_end_dttm_utc
			? moment(node.event_node_end_dttm_utc)
			: null;
      let diffMinutes: number | null = null
			if (end && end.isValid()) {
				diffMinutes = now.diff(end, 'minutes');
			}
			const startDiffMinutes = start.diff(now, 'minutes')

      let status: DispatchStatus = DispatchStatus.None;
			
			if (now.isSameOrAfter(start) && (!end || now.isBefore(end))) {
        status = DispatchStatus.Ongoing;
      } else if (diffMinutes !== null && diffMinutes >= 0 && diffMinutes <= 15) {
  		status = DispatchStatus.RecentlyEnded;
			}else if (startDiffMinutes >= 0) {
        status = DispatchStatus.Upcoming;
      }
	  		// UNCOMMENT FOR DEBUGGING PURPOSES ONLY
			// console.log(status)
			// console.log(diffMinutes)
			// console.log(dispatch.program_name)
      return {
        id: node.event_node_id,
        org: node.organization_name?.[0]?.label ?? '',
        site: node.site_display_label?.[0]?.label ?? '',
        program: dispatch.program_name?.[0]?.label ?? '',
        portfolio: dispatch.portfolio_name?.[0]?.label ?? '',
        startUtc: node.event_node_start_dttm_utc,
        endUtc: node.event_node_end_dttm_utc,
        status,
        timeZone: dispatch.full_time_zone ?? '',
      };
    })
  );
}

getDispatchesRows(args?: { limit: number }): Observable<DispatchViewModel[]> {
    return this.getDispatches({ limit: args?.limit }).pipe(
      map(res => {
        const rows = this.toDispatchRowsFromDispatches(res);
        return args?.limit ? rows.slice(0, args.limit) : rows;
      }),
      catchError(err => {
        console.error('Failed to load widgets', err);
        return of<DispatchViewModel[]>([]);
      })
    );
  }
}	